import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  StyledLink,
  StyledNavLink,
} from "../styledComponents/CommonStyledComponents";
import { useTranslation } from "react-i18next";
import {
  StyledQuickGameCategories,
  StyledQuickNavChips,
} from "../styledComponents/StyledGamesCategories";
import { useLocation } from "common/src/router/compat";

const ChevronIcon = ({ left }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d={left ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"} />
  </svg>
);

const pathOrFallback = (pages, key, currentLanguage, slug) =>
  (pages && pages[key]) || `/${currentLanguage}/${slug}`;

const QuickGameCategories = ({
  currentLanguage,
  games,
  allLinks,
  player,
  hideOnScroll,
  skin,
  royalePage,
  expandedNav,
  pages,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navRef = useRef(null);
  const chips = expandedNav;
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false });

  const updateScrollEdges = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    const left = el.scrollLeft > 4;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setScrollEdges((prev) =>
      prev.left === left && prev.right === right ? prev : { left, right },
    );
  }, []);

  useEffect(() => {
    if (!chips) return;
    updateScrollEdges();
    // link widths change once the web font loads
    if (document.fonts) document.fonts.ready.then(updateScrollEdges);
    window.addEventListener("resize", updateScrollEdges);
    return () => window.removeEventListener("resize", updateScrollEdges);
  }, [chips, updateScrollEdges, royalePage]);

  // labels change width when translations arrive or language switches
  useEffect(() => {
    if (chips) updateScrollEdges();
  });

  const scrollNav = (direction) => {
    const el = navRef.current;
    if (el) el.scrollBy({ left: direction * el.clientWidth * 0.6 });
  };

  useEffect(() => {
    if (!expandedNav) return;
    const container = navRef.current;
    if (!container) return;
    const activeEl = container.querySelector("a.active");
    if (activeEl) {
      activeEl.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [expandedNav, location.pathname, royalePage]);

  const handleLinkClick = (e, url) => {
    e.preventDefault();
    window.location.href = url;
  };

  if (expandedNav) {
    const lang = currentLanguage;
    const sportsBookItem = {
      key: "sports-book",
      to: `/${lang}/sports-book/sports`,
      label: t("label_sportsbook", { defaultValue: "Sportsbook" }),
      isActive: (_match, loc) => loc.pathname.includes("/sports-book"),
    };
    const baseItems = [
      ...(royalePage
        ? [
            {
              key: "99royale",
              to: royalePage,
              label: t("label_99royale", { defaultValue: "99Royale" }),
            },
          ]
        : []),
      ...(skin === "qbc"
        ? [
            {
              key: "games",
              to: pathOrFallback(pages, "games", lang, "games"),
              label: t("label_casino", { defaultValue: "Casino" }),
            },
          ]
        : []),
      {
        key: "slots",
        to: pathOrFallback(pages, "slots", lang, "slots"),
        label: t("label_slots", { defaultValue: "Slots" }),
      },
      {
        key: "video-slots",
        to: pathOrFallback(pages, "video-slots", lang, "video-slots"),
        label: t("label_video_slots", { defaultValue: "Video Slots" }),
      },
      {
        key: "livecasino",
        to: pathOrFallback(
          pages,
          skin === "yet" ? "live-casino" : "livecasino",
          lang,
          skin === "yet" ? "live-casino" : "livecasino",
        ),
        label: t("label_live_casino", { defaultValue: "Live Casino" }),
      },
      {
        key: "table-games",
        to: pathOrFallback(pages, "table-games", lang, "table-games"),
        label: t("label_table", { defaultValue: "Table" }),
      },
      {
        key: "slingo",
        to: pathOrFallback(pages, "slingo", lang, "slingo"),
        label: t("label_slingo", { defaultValue: "Slingo" }),
      },
    ];
    const sportsbookFirst =
      skin === "qbc" || skin === "fun" || skin === "hyc" || skin === "cac";
    const navItems = sportsbookFirst
      ? [sportsBookItem, ...baseItems]
      : skin === "abc"
        ? [...baseItems.slice(0, 1), sportsBookItem, ...baseItems.slice(1)]
        : [...baseItems, sportsBookItem];

    const nav = (
      <StyledQuickGameCategories
        ref={navRef}
        player={player}
        hideOnScroll={!hideOnScroll}
        onScroll={chips ? updateScrollEdges : undefined}
      >
        {navItems.map(({ key, to, label, isActive }) => (
          <StyledNavLink
            key={key}
            activeClassName="active"
            to={to}
            isActive={isActive}
          >
            {label}
          </StyledNavLink>
        ))}
      </StyledQuickGameCategories>
    );

    if (!chips) return nav;

    return (
      <StyledQuickNavChips
        $fadeLeft={scrollEdges.left}
        $fadeRight={scrollEdges.right}
      >
        {nav}
        {scrollEdges.left && (
          <button
            type="button"
            className="quick-nav-arrow left"
            aria-label="Scroll left"
            onClick={() => scrollNav(-1)}
          >
            <ChevronIcon left />
          </button>
        )}
        {scrollEdges.right && (
          <button
            type="button"
            className="quick-nav-arrow right"
            aria-label="Scroll right"
            onClick={() => scrollNav(1)}
          >
            <ChevronIcon />
          </button>
        )}
      </StyledQuickNavChips>
    );
  }

  return allLinks ? (
    <StyledQuickGameCategories player={player} hideOnScroll={!hideOnScroll}>
      <StyledNavLink
        as="div"
        quickSB={skin === "qbc" || skin === "bec"}
        activeClassName="active"
        className={
          location.pathname === `/${currentLanguage}/sports-book/sports` &&
          "active"
        }
        href={"/" + currentLanguage + "/sports-book/sports"}
        onClick={(e) =>
          handleLinkClick(e, "/" + currentLanguage + "/sports-book/sports")
        }
      >
        {t("label_sportsbook")}
      </StyledNavLink>
      <StyledNavLink
        activeClassName="active"
        to={"/" + currentLanguage + (games ? "/games" : "/index")}
      >
        {t("label_casino")}
      </StyledNavLink>
      <StyledNavLink
        activeClassName="active"
        to={"/" + currentLanguage + "/livecasino"}
      >
        {t("label_live_casino")}
      </StyledNavLink>
      {royalePage && (
        <StyledNavLink activeClassName="active" to={royalePage}>
          {t("label_99royale", { defaultValue: "99Royale" })}
        </StyledNavLink>
      )}
    </StyledQuickGameCategories>
  ) : (
    <StyledQuickGameCategories player={player} hideOnScroll={!hideOnScroll}>
      {location.pathname !== `/${currentLanguage}/livecasino` && (
        <StyledLink to={"/" + currentLanguage + "/livecasino"}>
          {t("label_live_casino")}
        </StyledLink>
      )}
      {(location.pathname === `/${currentLanguage}/livecasino` ||
        location.pathname === `/${currentLanguage}/sports-book/sports`) && (
        <StyledLink to={"/" + currentLanguage + (games ? "/games" : "/index")}>
          {t("label_casino")}
        </StyledLink>
      )}
      {location.pathname !== `/${currentLanguage}/sports-book/sports` && (
        <StyledLink to={"/" + currentLanguage + "/sports-book/sports"}>
          {t("label_sportsbook")}
        </StyledLink>
      )}
      {royalePage && location.pathname !== royalePage && (
        <StyledLink to={royalePage}>
          {t("label_99royale", { defaultValue: "99Royale" })}
        </StyledLink>
      )}
      {royalePage && location.pathname === royalePage && (
        <StyledLink to={"/" + currentLanguage + (games ? "/games" : "/index")}>
          {t("label_casino")}
        </StyledLink>
      )}
    </StyledQuickGameCategories>
  );
};

export default QuickGameCategories;
